import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Ticket,
  Mail,
  LineChart,
  HeartPulse,
  Smartphone,
  X,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/utils/telemetry';
import api from '@/utils/api';

/**
 * WhatsNewSplash — full-screen animated splash shown once per day after login.
 *
 * Behaviour:
 *   - Reads localStorage key `safer_splash_last_shown` (ISO date string YYYY-MM-DD).
 *   - If the stored value !== today's date, renders the splash and then writes today's date.
 *   - Carousel auto-advances every 5s and loops; user can click dots, prev/next or skip.
 *   - Dismiss updates the flag so it won't show again today.
 */

const STORAGE_KEY = 'safer_splash_last_shown';

/**
 * Each feature can declare an optional `variantB` to A/B-test against the
 * default copy. If `variantB` is omitted that feature is fixed (variant 'a'
 * always served). The bucket is sticky per-user (see `pickVariant`) so the
 * same user sees the same variant for the same feature across sessions.
 */
const FEATURES = [
  {
    id: 'gcal',
    icon: Calendar,
    eyebrow: 'Fresh off the press',
    title: 'Google Calendar Sync',
    description:
      'Connect once — every marketplace booking flows straight into your Google Calendar with full context, reminders and a deep-link back.',
    accent: 'from-blue-500/20 via-sky-500/10 to-transparent',
    ring: 'ring-blue-400/40',
    badge: 'bg-blue-500/15 text-blue-300',
    cta: { label: 'Connect calendar', to: '/settings' },
    variantB: {
      description:
        'Stop double-booking. SAFER-i™ pushes every confirmed booking into Google Calendar in real time — invitee details, reminders and a one-click deep-link included.',
      cta: { label: 'Sync my calendar' },
    },
  },
  {
    id: 'promo',
    icon: Ticket,
    eyebrow: 'Grow smarter',
    title: 'Self-serve Promo Codes',
    description:
      'Spin up branded discount codes for ala-carte services in seconds. Set cap, expiry, usage limits — we handle the rest.',
    accent: 'from-fuchsia-500/20 via-pink-500/10 to-transparent',
    ring: 'ring-fuchsia-400/40',
    badge: 'bg-fuchsia-500/15 text-fuchsia-300',
    cta: { label: 'Generate a code', to: '/settings' },
    variantB: {
      description:
        'Run your own promo without engineering. Caps, expiries and usage limits are baked in — share the code and watch redemptions roll in.',
      cta: { label: 'Create my first code' },
    },
  },
  {
    id: 'ics',
    icon: Mail,
    eyebrow: 'Zero manual work',
    title: 'Auto-email Calendar Invites',
    description:
      'Every confirmed booking ships with a standards-compliant .ics invite — Apple, Outlook and Google ready. No clicks required.',
    accent: 'from-emerald-500/20 via-teal-500/10 to-transparent',
    ring: 'ring-emerald-400/40',
    badge: 'bg-emerald-500/15 text-emerald-300',
    cta: { label: 'View bookings', to: '/marketplace' },
    variantB: {
      description:
        "Every booking now lands as a one-tap calendar invite in your client's inbox. Works across Apple, Outlook and Google — no copy-paste, no manual reminders.",
      cta: { label: 'See it in action' },
    },
  },
  {
    id: 'trends',
    icon: LineChart,
    eyebrow: 'See the signal',
    title: 'Growth Trends & CSV Export',
    description:
      'Week-over-week adoption curves powered by live telemetry. Export to CSV and drop into your boardroom deck.',
    accent: 'from-amber-500/20 via-orange-500/10 to-transparent',
    ring: 'ring-amber-400/40',
    badge: 'bg-amber-500/15 text-amber-300',
    cta: { label: 'Open trends', to: '/settings' },
    variantB: {
      description:
        "Boardroom-ready in seconds. Live week-over-week adoption charts, signup funnels and one-click CSV exports — all from your existing telemetry.",
      cta: { label: 'Show me the chart' },
    },
  },
  {
    id: 'health',
    icon: HeartPulse,
    eyebrow: 'Admin superpower',
    title: 'Metrics Health Dashboard',
    description:
      'One click — seven diagnostic pipelines, sub-300ms latency readings, and plain-English pass/fail across the stack.',
    accent: 'from-rose-500/20 via-red-500/10 to-transparent',
    ring: 'ring-rose-400/40',
    badge: 'bg-rose-500/15 text-rose-300',
    cta: { label: 'Run diagnostics', to: '/admin/metrics-health' },
    variantB: {
      description:
        "Catch silent failures before users do. One click runs every metrics pipeline end-to-end with latency stamps and clear pass/fail — no log diving required.",
      cta: { label: 'Smoke test now' },
    },
  },
  {
    id: 'mobile',
    icon: Smartphone,
    eyebrow: 'On-the-go polish',
    title: 'Mobile Smart Shortcuts',
    description:
      'Long-press the mobile menu to warp straight to your top 3 most-visited pages — learned from your own usage.',
    accent: 'from-violet-500/20 via-indigo-500/10 to-transparent',
    ring: 'ring-violet-400/40',
    badge: 'bg-violet-500/15 text-violet-300',
    cta: { label: 'Try it', to: '/dashboard' },
    variantB: {
      description:
        "Save 4 taps every visit. Long-press the mobile nav and SAFER-i™ hands you direct shortcuts to the 3 pages you actually use — auto-learned, zero setup.",
      cta: { label: 'Show me' },
    },
  },
];

const AB_SEED_KEY = 'safer_splash_ab_seed';

/** Stable 32-bit FNV-1a hash → unsigned int. */
function hashStr(input) {
  let h = 0x811c9dc5;
  const s = String(input);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h >>> 0;
}

/** Sticky A/B bucket per (user, featureId). Returns 'a' or 'b'.
 *  Persists a per-device seed so anonymous/cleared sessions still bucket
 *  consistently within the device. Always returns 'a' when no `variantB`
 *  is declared, so single-variant features keep their original behavior.
 */
function pickVariant(featureId, hasVariantB) {
  if (!hasVariantB) return 'a';
  let seed = '';
  try {
    const raw = sessionStorage.getItem('safer_user') || localStorage.getItem('safer_user');
    if (raw) {
      const u = JSON.parse(raw);
      seed = u?.user_id || u?.email || '';
    }
    if (!seed) {
      seed = localStorage.getItem(AB_SEED_KEY) || '';
      if (!seed) {
        seed = `dev-${Math.random().toString(36).slice(2)}-${Date.now()}`;
        localStorage.setItem(AB_SEED_KEY, seed);
      }
    }
  } catch {
    seed = 'fallback';
  }
  return hashStr(`${seed}|${featureId}`) % 2 === 0 ? 'a' : 'b';
}

const todayKey = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export default function WhatsNewSplash() {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [entered, setEntered] = useState(false);
  const [aOverrides, setAOverrides] = useState({});
  const [bOverrides, setBOverrides] = useState({});
  const navigate = useNavigate();

  const total = FEATURES.length;
  const baseFeature = useMemo(() => FEATURES[index], [index]);

  // Sticky A/B bucket assignment for the *whole splash session*. Computed once
  // per mount keyed on the user seed; ensures view + cta + dismiss events for
  // a feature share the same variant tag. Whether B has any copy at all is
  // recomputed when overrides arrive (B might exist via DB even if the in-code
  // `variantB` is missing).
  const bucketMap = useMemo(() => {
    const out = {};
    for (const f of FEATURES) {
      const hasB = !!(f.variantB || bOverrides[f.id]);
      out[f.id] = pickVariant(f.id, hasB);
    }
    return out;
  }, [bOverrides]);

  // Fetch admin-authored overrides once. A-overrides are promoted winners;
  // B-overrides are the current B-variant copy. Both take priority over the
  // hardcoded WhatsNewSplash content.
  useEffect(() => {
    let cancelled = false;
    api
      .silentRequest({ method: 'GET', url: '/splash/cards/active' })
      .then((res) => {
        if (cancelled) return;
        setAOverrides(res?.data?.a_overrides || {});
        setBOverrides(res?.data?.b_overrides || {});
      })
      .catch(() => { /* swallow — fall back to hardcoded defaults */ });
    return () => { cancelled = true; };
  }, []);

  // Resolved copy for the current slide based on its A/B bucket.
  // A copy: `a_overrides[id]` (promoted baseline) wins over the hardcoded card.
  // B copy: `b_overrides[id]` (current B variant) wins over hardcoded `variantB`.
  const feature = useMemo(() => {
    const variant = bucketMap[baseFeature.id] || 'a';
    if (variant === 'a') {
      const aOv = aOverrides[baseFeature.id];
      if (aOv) {
        return {
          ...baseFeature,
          description: aOv.description,
          cta: { ...baseFeature.cta, label: aOv.cta_label },
          _variant: 'a',
        };
      }
      return { ...baseFeature, _variant: 'a' };
    }
    const dbOverride = bOverrides[baseFeature.id];
    const variantB = baseFeature.variantB || {};
    const description = dbOverride?.description ?? variantB.description ?? baseFeature.description;
    const ctaLabel = dbOverride?.cta_label ?? variantB.cta?.label ?? baseFeature.cta.label;
    return {
      ...baseFeature,
      description,
      cta: { ...baseFeature.cta, label: ctaLabel },
      _variant: 'b',
    };
  }, [baseFeature, bucketMap, aOverrides, bOverrides]);

  // Decide whether to show once per day
  useEffect(() => {
    try {
      const last = localStorage.getItem(STORAGE_KEY);
      if (last !== todayKey()) {
        // Mark as shown immediately so navigating across protected routes
        // within the same day doesn't replay the splash from slide 0.
        localStorage.setItem(STORAGE_KEY, todayKey());
        setOpen(true);
        // Kick off the enter animation next frame
        requestAnimationFrame(() => setEntered(true));
      }
    } catch {
      // localStorage blocked — fail closed (don't show)
    }
  }, []);

  // Auto-advance every 5s while open
  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % total);
    }, 5000);
    return () => clearInterval(id);
  }, [open, total]);

  // Emit a telemetry event whenever the visible slide changes (including first).
  // Used by /api/audit/splash/conversion to compute per-feature view → CTA funnel,
  // and pivoted by `value` (variant tag) for the A/B harness.
  useEffect(() => {
    if (!open || !feature?.id) return;
    trackEvent('splash_view', feature.id, feature._variant);
  }, [open, feature?.id, feature?._variant]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') handleDismiss();
      if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % total);
      if (e.key === 'ArrowLeft') setIndex((i) => (i - 1 + total) % total);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, total]);

  const handleDismiss = () => {
    trackEvent('splash_dismiss', feature?.id || 'unknown', feature?._variant || 'a');
    // Flag is already set on mount; just animate out.
    setEntered(false);
    // Let the exit transition finish before unmounting
    setTimeout(() => setOpen(false), 250);
  };

  const handleCta = () => {
    const target = feature?.cta?.to;
    trackEvent('splash_cta', feature.id, feature._variant);
    // handleDismiss fires splash_dismiss — skip it by inlining the animate-out.
    setEntered(false);
    setTimeout(() => setOpen(false), 250);
    if (target) {
      setTimeout(() => navigate(target), 260);
    }
  };

  if (!open) return null;

  const Icon = feature.icon;

  return (
    <div
      data-testid="whats-new-splash"
      role="dialog"
      aria-modal="true"
      aria-labelledby="whats-new-title"
      className={`fixed inset-0 z-[9999] flex items-center justify-center px-4 py-6 transition-opacity duration-300 ${
        entered ? 'opacity-100' : 'opacity-0'
      }`}
      style={{
        background:
          'radial-gradient(ellipse at top, rgba(30,41,59,0.85), rgba(2,6,23,0.96) 60%)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
      }}
    >
      {/* Ambient animated orbs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl animate-pulse" />
        <div
          className="absolute top-1/3 -right-24 h-80 w-80 rounded-full bg-fuchsia-500/20 blur-3xl animate-pulse"
          style={{ animationDelay: '0.8s' }}
        />
        <div
          className="absolute -bottom-24 left-1/3 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl animate-pulse"
          style={{ animationDelay: '1.6s' }}
        />
      </div>

      {/* Card */}
      <div
        className={`relative w-full max-w-3xl transform overflow-hidden rounded-3xl border border-white/10 bg-slate-950/80 shadow-2xl transition-all duration-500 ${
          entered ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-6 scale-95 opacity-0'
        }`}
      >
        {/* Accent gradient tied to current feature */}
        <div
          key={feature.id}
          className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${feature.accent} transition-opacity duration-700`}
        />

        {/* Close */}
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="whats-new-close-btn"
          aria-label="Close"
          className="absolute right-4 top-4 z-10 rounded-full bg-white/5 p-2 text-white/70 transition hover:bg-white/10 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="relative z-[1] flex flex-col gap-8 p-8 sm:p-12">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/5 ring-1 ring-white/10">
              <Sparkles className="h-4 w-4 text-amber-300" />
            </div>
            <div className="flex flex-col">
              <span className="text-[11px] uppercase tracking-[0.22em] text-white/50">
                Welcome back
              </span>
              <span className="text-xs text-white/70">
                Here's what's new on SAFER-i™ today
              </span>
            </div>
          </div>

          {/* Content — keyed by feature.id so Tailwind re-runs the enter animation */}
          <div key={feature.id} className="flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
            <div className="flex items-start gap-5">
              <div
                className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-white/5 ring-1 ${feature.ring}`}
              >
                <Icon className="h-8 w-8 text-white" />
              </div>
              <div className="min-w-0">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ${feature.badge}`}
                >
                  {feature.eyebrow}
                </span>
                <h2
                  id="whats-new-title"
                  data-testid="whats-new-title"
                  className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl"
                >
                  {feature.title}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-white/70 sm:text-base">
                  {feature.description}
                </p>
              </div>
            </div>

            {/* CTA row */}
            <div className="flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setIndex((i) => (i - 1 + total) % total)}
                  data-testid="whats-new-prev-btn"
                  aria-label="Previous feature"
                  className="h-9 w-9 rounded-full p-0 text-white/70 hover:bg-white/10 hover:text-white"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div
                  className="flex items-center gap-1.5"
                  role="tablist"
                  aria-label="Feature slides"
                >
                  {FEATURES.map((f, i) => (
                    <button
                      key={f.id}
                      type="button"
                      role="tab"
                      aria-selected={i === index}
                      aria-label={`Show ${f.title}`}
                      onClick={() => setIndex(i)}
                      data-testid={`whats-new-dot-${i}`}
                      className={`h-1.5 rounded-full transition-all ${
                        i === index ? 'w-6 bg-white' : 'w-1.5 bg-white/30 hover:bg-white/60'
                      }`}
                    />
                  ))}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setIndex((i) => (i + 1) % total)}
                  data-testid="whats-new-next-btn"
                  aria-label="Next feature"
                  className="h-9 w-9 rounded-full p-0 text-white/70 hover:bg-white/10 hover:text-white"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleDismiss}
                  data-testid="whats-new-skip-btn"
                  className="text-white/70 hover:bg-white/10 hover:text-white"
                >
                  Not now
                </Button>
                <Button
                  type="button"
                  onClick={handleCta}
                  data-testid="whats-new-cta-btn"
                  className="group bg-white text-slate-950 hover:bg-white/90"
                >
                  {feature.cta.label}
                  <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Footer progress bar */}
          <div className="h-0.5 w-full overflow-hidden rounded-full bg-white/5">
            <div
              key={`pb-${feature.id}`}
              className="h-full bg-gradient-to-r from-white/80 to-white/30"
              style={{
                width: '100%',
                transformOrigin: 'left',
                animation: 'whatsNewProgress 5s linear forwards',
              }}
            />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes whatsNewProgress {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
      `}</style>
    </div>
  );
}
